/**
 * Predictive search overlay (task C5).
 *
 * Results are rendered by Shopify's Predictive Search API through the theme's
 * own section, so no product data is templated in JavaScript. This file owns
 * the debounce, the request lifecycle, keyboard navigation and recent searches.
 *
 * Progressive enhancement: the form is a real GET to /search. With JS off the
 * overlay never opens and the header search icon still leads somewhere useful.
 */
(function () {
  'use strict';

  var V = window.Velocity;
  if (!V) return;

  var DEBOUNCE_MS = 200;              // per the C5 spec
  var MIN_QUERY = 2;
  var RECENT_KEY = 'velocity:recent-searches';
  var RECENT_MAX = 5;

  class PredictiveSearch extends V.Element {
    init() {
      this.input = this.querySelector('[data-search-input]');
      this.resultsEl = this.querySelector('[data-search-results]');
      this.statusEl = this.querySelector('[data-search-status]');
      this.recentEl = this.querySelector('[data-search-recent]');
      this.recentList = this.querySelector('[data-search-recent-list]');
      this.form = this.querySelector('[data-search-form]');

      this.activeIndex = -1;
      this.controller = null;

      var self = this;

      // Header trigger opens the overlay and focuses the field.
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-search-open]'),
        function (trigger) {
          self.on(trigger, 'click', function (e) {
            e.preventDefault();
            V.drawers.open(self, trigger);
            self.renderRecent();
            // Focus after the trap has run so it is not immediately overridden.
            setTimeout(function () { self.input.focus(); }, 0);
          });
        }
      );

      this.on(this.input, 'input', V.debounce(this.onInput.bind(this), DEBOUNCE_MS));
      this.on(this.input, 'keydown', this.onKeydown.bind(this));
      this.on(this.form, 'submit', this.onSubmit.bind(this));

      var clear = this.querySelector('[data-search-clear]');
      if (clear) this.on(clear, 'click', this.clearRecent.bind(this));

      this.on(this.resultsEl, 'click', function (e) {
        var link = e.target.closest('a[href]');
        if (link) self.remember(self.input.value);
      });

      this.listen(V.EVENTS.drawerClose, function (data) {
        if (data.drawer === self) self.reset();
      });
    }

    reset() {
      this.abort();
      this.input.value = '';
      this.resultsEl.innerHTML = '';
      this.setExpanded(false);
      this.activeIndex = -1;
    }

    abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    setExpanded(isExpanded) {
      this.input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }

    onInput() {
      var q = this.input.value.trim();

      if (q.length < MIN_QUERY) {
        this.abort();
        this.resultsEl.innerHTML = '';
        this.setExpanded(false);
        this.renderRecent();
        return;
      }

      this.search(q);
    }

    search(query) {
      var self = this;

      // Cancel any in-flight request so a slow early response cannot overwrite
      // the results for a newer query.
      this.abort();
      this.controller = new AbortController();

      if (this.recentEl) this.recentEl.hidden = true;

      var url = window.Velocity.routes.predictiveSearch +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=6' +
        '&section_id=predictive-search-results';

      return fetch(url, { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Search failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var fragment = document.createRange().createContextualFragment(html);
          var payload = fragment.querySelector('[data-search-payload]');
          self.resultsEl.innerHTML = payload ? payload.innerHTML : '';
          self.activeIndex = -1;
          self.setExpanded(true);
          self.announceCount();
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;   // superseded, not a failure
          self.resultsEl.innerHTML = '';
          self.setExpanded(false);
          self.setStatus(V.strings ? V.strings.cartError : '');
        });
    }

    options() {
      return Array.prototype.slice.call(
        this.resultsEl.querySelectorAll('[role="option"]')
      );
    }

    announceCount() {
      var n = this.options().length;
      this.setStatus(
        n === 0
          ? this.resultsEl.dataset.emptyText || ''
          : n + ' results'
      );
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text || '';
    }

    /** Roving selection through results with Up/Down, per the combobox pattern. */
    onKeydown(e) {
      var options = this.options();

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!options.length) return;
        e.preventDefault();
        var delta = e.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
        this.highlight(options);
        return;
      }

      if (e.key === 'Enter' && this.activeIndex > -1 && options[this.activeIndex]) {
        e.preventDefault();
        this.remember(this.input.value);
        var link = options[this.activeIndex].querySelector('a[href]') || options[this.activeIndex];
        if (link.href) window.location.href = link.href;
      }
    }

    highlight(options) {
      var self = this;
      options.forEach(function (option, i) {
        var isActive = i === self.activeIndex;
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        option.classList.toggle('is-active', isActive);
        if (isActive) {
          self.input.setAttribute('aria-activedescendant', option.id || '');
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    onSubmit() {
      this.remember(this.input.value);
      // Let the form submit normally to /search.
    }

    /* ---- recent searches (this browser only, never transmitted) ---------- */
    readRecent() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      } catch (e) {
        return [];
      }
    }

    remember(term) {
      term = (term || '').trim();
      if (term.length < MIN_QUERY) return;
      var list = this.readRecent().filter(function (t) { return t !== term; });
      list.unshift(term);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) {
        /* storage unavailable — recents just will not persist */
      }
    }

    clearRecent() {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch (e) { /* no-op */ }
      this.renderRecent();
      this.input.focus();
    }

    renderRecent() {
      if (!this.recentEl || !this.recentList) return;
      var list = this.readRecent();

      if (!list.length || this.input.value.trim().length >= MIN_QUERY) {
        this.recentEl.hidden = true;
        return;
      }

      var self = this;
      this.recentList.innerHTML = '';
      list.forEach(function (term) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'search-overlay__recent-item';
        btn.textContent = term;          // textContent, never innerHTML
        btn.addEventListener('click', function () {
          self.input.value = term;
          self.search(term);
        });
        li.appendChild(btn);
        self.recentList.appendChild(li);
      });

      this.recentEl.hidden = false;
    }
  }

  V.define('predictive-search', PredictiveSearch);
})();
